import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid, Stack, styled, Typography, Skeleton } from '@mui/material'

import { useFeatureValue } from '@growthbook/growthbook-react'
import { GROWTHBOOK_PROMO_FINANCING } from '@constants/growthbook-flags'
import useGetPromotions from '@hooks/useGetPromotions'
import { currencyFormatUS } from '@helpers/string-helper'
import { getRegionZone } from '@helpers/geo-location'
import { fetchPromotions } from '@services/checkout'
import { productFinancing } from '@helpers/finance'
import { productUrl } from '@helpers/route'
import RTGLink from '../../shared/link'
import PriceAtom from '../../../@rtg2022/components/atoms/Price/Price'

const StyledProduct = styled(Stack)(({ theme }) => ({
  padding: '0 !important',
  marginBottom: '10px',
  h3: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 'normal',
  },
}))

const StyledPrice = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 'normal',
  marginTop: '5px',
}))

const StyledImage = styled('div')(({ theme }) => ({
  paddingRight: '1em',
  width: '120px',
  textAlign: 'center',
  img: {
    maxHeight: '100px',
    maxWidth: '100px',
  },
}))

const StyledTitlePrice = styled('div')(({ theme }) => ({
  marginLeft: '0 !important',
  lineHeight: 'normal',
}))

const StyledPromoLabel = styled('span')(({ theme }) => ({
  padding: '5px 10px',
  backgroundColor: theme.palette.error.main,
  fontStyle: 'normal',
  fontWeight: 600,
  borderRadius: '4px',
  color: '#FFF',
  marginRight: '9px',
  display: 'inline-block',
}))

const SkeletonPrice = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
}))

const ProductPromotion = props => {
  const { product, promo, qualifierQuantity, targetQuantity, promoType } = props
  const [promotion, setPromotion] = useState(product?.promotionData)
  const [finance, setFinance] = useState(null)
  const promoFinancing = useFeatureValue(GROWTHBOOK_PROMO_FINANCING)

  const isBuyXAndYGetZ = useCallback(() => promoType === 'BUY X AND Y GET Z DISCOUNT', [promoType])
  const requestItems = [
    {
      sku: product.sku,
      quantity: qualifierQuantity && parseInt(qualifierQuantity),
    },
    {
      sku: promo && promo.sku,
      quantity: isBuyXAndYGetZ() ? 2 : targetQuantity && parseInt(targetQuantity),
    },
  ]

  const { data: promotionData, isLoading, isError } = useGetPromotions(
    requestItems,
    getRegionZone().region,
    getRegionZone().zone,
  )

  useEffect(() => {
    if (promotionData) {
      setPromotion(promotionData)
      const newFinance = productFinancing(
        promotionData.cartTotal,
        product?.delivery_type,
        false,
        true,
        0,
        promoFinancing,
      )
      setFinance(newFinance)
    }
  }, [promotionData, product, promoFinancing])

  const promoRoute = promo?.route ? promo.route : null
  const promoProduct = promotion ? promotion.lineItems.find(item => item.promotionStatus === 'APPLIED') : null

  const renderProduct = (image, title, sku, price, promoLabel) => (
    <StyledProduct direction="row" spacing={1}>
      <StyledImage>
        {isLoading ? <Skeleton width="100%" height="85px" /> : <img src={`${image}&h=150`} alt={title || sku} />}
      </StyledImage>
      {isLoading ? (
        <SkeletonPrice>
          <Skeleton sx={{ fontSize: '16px' }} width="9.375rem" />
          <Skeleton sx={{ fontSize: '14px' }} width="6.25rem" />
        </SkeletonPrice>
      ) : (
        <StyledTitlePrice>
          <Typography variant="h3">
            {promoLabel && <StyledPromoLabel>{promoLabel}</StyledPromoLabel>}
            <span dangerouslySetInnerHTML={{ __html: title }} />
          </Typography>
          <StyledPrice>
            <PriceAtom price={currencyFormatUS(price)} />
          </StyledPrice>
        </StyledTitlePrice>
      )}
    </StyledProduct>
  )

  return (
    <>
      {promotion && promotion.totalSavings > 0 && (
        <Grid item xs={12} md={8}>
          {isBuyXAndYGetZ() && (
            <RTGLink
              data={{
                slug: productUrl(promoRoute),
                altDesc: promo.title ? promo.title : '',
              }}
            >
              {renderProduct(
                promo.image,
                promo.title,
                promo.sku,
                promotion.lineItems.length > 2 ? promotion.lineItems[1]?.unitPrice : promotion.lineItems[0]?.unitPrice,
              )}
            </RTGLink>
          )}

          {/* Target Content */}
          <RTGLink
            data={{
              slug: productUrl(promoRoute),
              altDesc: promo.title ? promo.title : '',
            }}
          >
            {renderProduct(promo.image, promo.title, promo.sku, promoProduct.bonusPrice, 'BONUS BUY INCLUDED')}
          </RTGLink>
        </Grid>
      )}
    </>
  )
}

ProductPromotion.propTypes = {
  promoType: PropTypes.string,
  product: PropTypes.object,
  promo: PropTypes.object,
  qualifierQuantity: PropTypes.string,
  targetQuantity: PropTypes.string,
}

export default ProductPromotion
